import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import { useAutoTaggingPlans } from "../../../hooks/useAutoTaggingPlans";
import { AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE } from "../../../services/autoTaggingService";
import { AI_PLAN_ADD_ITEMS_PATH } from "../constants";
import TaggingPlanFilterBar from "./TaggingPlanFilterBar";
import TaggingPlanTable from "./TaggingPlanTable";

export default function TaggingPlanTab() {
  const navigate = useNavigate();
  const [objectType, setObjectType] = useState("");
  const [objectSubType, setObjectSubType] = useState("");
  const [conceptCode, setConceptCode] = useState("");
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: AUTO_TAGGING_PLANS_DEFAULT_PAGE_SIZE,
  });
  const { planTypes, planTypesLoading } = useAssessmentPlanFilterOptions();
  const {
    rows,
    total,
    loading: plansLoading,
    hasLoaded: plansHasLoaded,
    reload,
  } = useAutoTaggingPlans({
    objectType,
    objectSubType,
    concept: conceptCode,
    planTypes,
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
  });

  // Filters change → reset to first page for a new server query.
  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [objectType, objectSubType, conceptCode]);

  // Keep the grid mounted on refresh/delete so pagination (e.g. page 2) is preserved.
  const showInitialLoading =
    (!plansHasLoaded && plansLoading) || planTypesLoading;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flex: 1,
        minHeight: 0,
        mx: "16px",
      }}
    >
      <TaggingPlanFilterBar
        objectType={objectType}
        objectSubType={objectSubType}
        conceptCode={conceptCode}
        onObjectTypeChange={(nextObjectType, nextObjectSubType) => {
          setObjectType(nextObjectType);
          setObjectSubType(nextObjectSubType);
          setConceptCode("");
        }}
        onObjectSubTypeChange={(nextObjectSubType) => {
          setObjectSubType(nextObjectSubType);
          setConceptCode("");
        }}
        onConceptCodeChange={setConceptCode}
      />
      {showInitialLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TaggingPlanTable
          rows={rows}
          rowCount={total}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onAddNew={() => navigate(AI_PLAN_ADD_ITEMS_PATH)}
          onPlanDeleted={reload}
          onPlanUpdated={reload}
        />
      )}
    </Box>
  );
}
